import React from 'react';
import {
    Card,
    WarningModal,
    Checkbox,
    ApplicantExamDetailsTabs,
    AlertText,
    ExamApplicationStatus,
    ExamStatus,
    ExamType,
    AppType,
} from 'components';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Dispatch, IRootState } from '../../redux/store/configureStore';
import { RegistrationForExamContainer } from '../RegistrationForExamContainer';
import { useLocalErrorHandler } from 'hooks';
import { SystemSettings } from '../../components/SystemSettings';
import { IApplicantExamDetails } from '../../api/ApiClient';
import moment from 'moment';
import styles from './ExamDetailsContainer.module.scss';

const ExamDetailsContainer = ({
    setErrorHandler,
    fetchExamDetails,
    examId,
    exam,
    examApplication,
    onPush,
    apiError,
    createExamApplication,
    onlineExamRedirectionUrl,
    generateOnlineExamRedirectionUrl,
}: Props) => {
    useLocalErrorHandler(setErrorHandler);

    const [processing, setProcessing] = React.useState(false);
    const [showRegisterInfo, setShowRegisterInfo] = React.useState(false);
    const [showRulesModal, setShowRulesModal] = React.useState(false);
    const [complianceRules, setComplianceRules] = React.useState(false);
    const [showSystem, setShowSystem] = React.useState(false);

    const redirectionLink = React.useRef<HTMLAnchorElement | null>(null);
    const examStatus = React.useMemo(() => exam && exam.examStatus, [exam]);
    const examApplicationStatus = React.useMemo(() => examApplication && examApplication.status, [examApplication]);
    const draft = React.useMemo(() => examApplicationStatus === ExamApplicationStatus.Draft, [examApplicationStatus]);
    const title = React.useMemo(() => (exam ? exam.qualificationTitle : ''), [exam]);
    const changeStatus = React.useMemo(() => !examApplicationStatus || draft || examApplicationStatus === ExamApplicationStatus.CorrectionsRequired, [examApplicationStatus, draft]);

    const urlParams = new URLSearchParams(window.location.search);
    const from = urlParams.get('from');

    React.useEffect(() => {
        setShowSystem(false);
        if (exam && notReadyForOnlineExam(exam)) {
            setShowSystem(true);
        }
    }, [exam]);

    const handleCloseShowSystem = React.useCallback(() => {
        if (exam && !notReadyForOnlineExam(exam)) {
            setShowSystem(false);
        }
    }, [exam]);

    React.useEffect(() => {
        const fetch = async () => {
            setProcessing(true);
            await fetchExamDetails(examId);
            setProcessing(false);
        };

        fetch();
    }, []);

    React.useEffect(() => {
        if (redirectionLink.current && onlineExamRedirectionUrl) {
            redirectionLink.current.click();
        }
    }, [redirectionLink, onlineExamRedirectionUrl]);

    const goBack = () => {
        onPush(`/${from}`);
    };

    const checkApplicantReadyStatus = () => examApplicationStatus && (
        examApplicationStatus === ExamApplicationStatus.Paid
        || examApplicationStatus === ExamApplicationStatus.IsPassing
        || examApplicationStatus === ExamApplicationStatus.AdmittedToExam);

    const checkExamReadyStatus = () => examStatus && (
        examStatus === ExamStatus.ApplyingFinished
        || examStatus === ExamStatus.IsPassing
    );

    const onRegisterButtonClick = async () => {
        if (!draft && examStatus === ExamStatus.IsApplying) {
            await createExamApplication(examId);
        }
        setShowRegisterInfo(true);
    };

    const notReadyForOnlineExam = (app: IApplicantExamDetails) => {
        const readyForOnlineExam = localStorage.getItem('readyForOnlineExam');
        return !readyForOnlineExam && app.examType === ExamType.Online && app.examApplicationStatus === ExamApplicationStatus.Paid;
    };

    const cardHeaderBtn = () => (
        <>
            {changeStatus ? (
                <Button onClick={onRegisterButtonClick}>
                    {examApplicationStatus === ExamApplicationStatus.CorrectionsRequired
                        ? 'Перейти к исправлению'
                        : `${draft ? 'Редактировать' : 'Подать'} заявку`
                    }
                </Button>
            ) : null
            }
            {exam && exam.examType === ExamType.Online && checkApplicantReadyStatus() && checkExamReadyStatus() ? (
                <Button className="ml-4" onClick={() => setShowRulesModal(true)}>
                    Начать сдачу экзамена
                </Button>
            ) : null}
        </>
    );

    const renderRulesModal = () => (
        <WarningModal
          title="Правила проведения экзамена"
          show={showRulesModal}
          onHide={() => setShowRulesModal(false)}
          titleClassNames={styles.ruleModalTitle}
          bodyClassNames="pb-0 pt-0"
          footerClassNames="p-0"
          dialogClassName={styles.rulesModalDialog}
          withoutCancelButton
          large
        >
            <span className={styles.rulesCategoryTitle}>Пожалуйста, подтвердите, что вы внимательно ознакомились и согласны со всеми правилами проведения онлайн экзамена.</span>
            <div className={styles.ruleModalTextBlock}>
                <span>
                    Настоящие Условия и Правила проведения экзамена (далее - &quot;Правила&quot;) определяют порядок взаимодействия Вас, в качестве Соискателя (далее - &quot;Соискателя&quot; или &quot;Вас&quot;) и Центра оценки квалификации (далее - &quot;ЦОК&quot;) при использовании программного обеспечения &quot;Автоматизированная система ЦОК&quot; (далее - &quot;АС ЦОК&quot;) при прохождении процедуры независимой оценки квалификации в онлайн режиме (далее - &quot;Онлайн НОК&quot; или &quot;Экзамен&quot;). Настоящие Правила также применяются ко всей информации (включая текстовую, графическую и визуальную), участвующей в процессе или появившейся в результате взаимодействия Соискателя с АС ЦОК (далее - &quot;Контент&quot;).
                    <br />
                    <br />
                    Для использования АС ЦОК Соискатель должен обладать компьютерным и программным обеспечением, а также Интернет-соединением, соответствующими техническим требованиям для работы в АС ЦОК в рамках прохождении процедуры независимой оценки квалификации в онлайн режиме:
                    <br />
                    <br />
                    <ul className="mb-0">
                        <li>
                            Разрешение и fps веб-камеры<br />
                            Минимальное: 720p 30fps<br />
                            Рекомендуемое: 1080p 30fps<br />
                        </li>
                        <br />

                        <li>Тип интернета: Выделенная линия (Ethernet или оптоволоконный канал)</li>
                        <br />

                        <li>
                            Скорость интернета на скачивание:<br />
                            Минимальное: 5 Mbps<br />
                            Рекомендуемое: 10 Mbps<br />
                        </li>
                        <br />

                        <li>
                            Скорость интернета на загрузку:<br />
                            Минимальное: 5 Mbps<br />
                            Рекомендуемое: 10 Mbps<br />
                        </li>
                        <br />

                        <li>
                            Оперативная память компьютера (RAM):<br />
                            Минимальное: 2 GB<br />
                            Рекомендуемое: 4 GB<br />
                        </li>
                        <br />

                        <li>
                            Процессорная частота<br />
                            Минимальное: Одноядерный 1 ГГц или выше<br />
                            Рекомендуемое: Двухъядерный 2 ГГц или выше (i3 / i5 / i7 или AMD)<br />
                        </li>
                        <br />

                        <li>
                            Доступность портов: 1935, 843, 80, 443, 61613, UDP/TCP 1935, 843, 80, 443, 61613, UDP/TCP<br />
                        </li>
                        <br />

                        <li>
                            Браузер: Google Chrome последней версии<br />
                        </li>
                        <br />

                        <li>
                            Исправно функционирующий микрофон (внешний или встроенный в веб-камеру)
                        </li>
                        <br />
                    </ul>
                    Соискатель также должен обеспечить приемлемые внешние условия на всем протяжении Онлайн НОК, включая, но не ограничиваясь:
                    <br />
                    <br />
                    <ul className="mb-0">
                        <li>хорошее освещение помещения, в котором будет проходить Онлайн НОК</li>
                        <li>отсутствие каких-либо шумовых загрязнений</li>
                        <li>отсутствие каких-либо помех передаче видео и/или аудио сигнала</li>
                    </ul>
                    <br />
                    ЦОК и/или Разработчик АС ЦОК не несут никакой ответственности за любые технические, сетевые или программные сбои, которые могут каким-либо образом повлиять на процесс прохождения Соискателем процедуры независимой оценки квалификации в онлайн режиме.
                    <br />
                    <br />
                    Продолжая работу в АС ЦОК, Вы принимаете настоящие Правила и соглашаетесь неукоснительно им следовать. В случае Вашего несогласия с настоящими Правилами, немедленно прекратите использование АС ЦОК, осуществив выход из Вашего личного кабинета (далее - &quot;ЛК&quot;).
                    <br />
                    <br />
                    ЦОК оставляет за собой право в любой момент приостановить или прекратить предоставление доступа Соискателю к АС ЦОК без какого-либо предварительного уведомления Соискателя в случае, если ЦОК имеет основание полагать, что имело место нарушение настоящих Правил Соискателем.
                    <br />
                    <br />
                    Соискатель несет полную ответственность за использование АС ЦОК, а также за все последствия, которые могут возникнуть в результате этого использования.
                    <br />
                    <br />
                    Весь Контент, включая видеозапись Онлайн НОК Соискателя будет передан и просмотрен сотрудниками ЦОК, членами экспертной комиссии НОК, а также СПКФР. Сущность, функциональный набор, а также состав самих сервисов, входящих в состав АС ЦОК может быть изменен в любое время без предварительного уведомления Соискателя. Также, ЦОК может в любой момент приостановить или прекратить предоставление доступа Соискателю к АС ЦОК без какого-либо предварительного уведомления Соискателя.
                    <br />
                    <br />
                    Соискателю запрещается вести фото и/или видео съемку, а также звуковую запись любого типа взаимодействия с АС ЦОК без письменного разрешения Разработчика АС ЦОК. Вы также не имеете права частично или полностью копировать вопросы и/или ответы из Экзамена, делиться ими, а также обсуждать их с кем-либо.
                    <br />
                    <br />
                    Продолжая работу в АС ЦОК, Вы соглашаетесь предоставлять только правдивую и точную информацию в целях корректной идентификации ЦОК Вашей личности.
                    <br />
                    <br />
                    Находясь в режиме Онлайн НОК, АС ЦОК будет удаленно подсоединяться к Вашему компьютеру в целях предоставления возможности Экспертам осуществлять мониторинг всего, что происходит на экране Вашего компьютера, а также Вашего комнатного окружения. Как часть процесса Онлайн НОК, Вы соглашаетесь содействовать Эксперту, проводящему экзамен (далее - &quot;Эксперт НОК&quot;) в осмотре Вашего комнатного окружения при помощи веб-камеры. Вы также соглашаетесь поддерживать аудио и визуальный контакт с Экспертом НОК на протяжении всего процесса Онлайн НОК, а также позволяете АС ЦОК вести запись всего Экзамена, включая запись экрана Вашего компьютера. Вы также соглашаетесь с тем, что данные записи будут переданы на обработку в СПКФР.
                    <br />
                    <br />
                    В случае некорректного поведения Соискателя во время Онлайн НОК, Эксперт НОК оставляет за собой право прекратить предоставление доступа к АС ЦОК.
                    Продолжая использование АС ЦОК, Вы понимаете и принимаете, что Экзамен может быть прерван Экспертом НОК, включая, но не ограничиваясь следующими нарушениями со стороны Соискателя:
                    <br />
                    - Нарушение этических норм поведения<br />
                    - Уклонение из зоны видимости веб-камеры<br />
                    - Отключение микрофона<br />
                    - Использование более одного средства ввода/вывода информации на компьютер<br />
                    - Общение с третьими лицами в любой форме<br />
                    - Предоставление доступа к своему компьютеру третьим лицам<br />
                    - Использование вспомогательных или справочных материалов в любой форме<br />
                    - Использование иных технически сложных устройств, помимо компьютера, на котором осуществляется Онлайн НОК.<br />
                    - Намеренное игнорирование сообщений со стороны Эксперта НОК<br />
                    - Многократный выход курсора из зоны Экзамена.
                    <br />
                    <br />
                    Вы соглашаетесь и принимаете тот факт, что Разработчик АС ЦОК может в любое время вносить изменения в техническую и функциональную спецификацию АС ЦОК по своему усмотрению, а также с тем, что ЦОК и/или Разработчик АС ЦОК не несут какой-либо ответственности за недоступность или невозможность использовать АС ЦОК по любым причинам.
                    <br />
                    <br />
                    Вы отчетливо понимаете и соглашаетесь с тем, что вся информация, вносимая Вами в АС ЦОК будет передаваться через сеть Интернет и процесс этой передачи находится вне зоны контроля ЦОК и/или Разработчика АС ЦОК. В этой связи, у ЦОК и/или Разработчика АС ЦОК не может возникать никаких обязательств или ответственности за возможную потерю, удаление или отсутствие возможности передать или сохранить такой Контент.
                    ЦОК и/или Разработчика АС ЦОК не несут никакой ответственности за потерю или повреждение Ваших компьютерных систем, электронно-цифровых устройств или компьютерных сетей, при использование Вами АС ЦОК.
                    ЦОК и/или Разработчика АС ЦОК также не несут никакой ответственности за последствия или ущерб, нанесенный возможными DDoS-атаками, вирусами или любыми другими технически опасными средствами, способными заразить и повредить Ваше компьютерное оборудование, программы, устройства или данные, в результате использования АС ЦОК.
                    <br />
                    <br />
                    ЦОК и/или Разработчик АС ЦОК ни при каких обстоятельствах не несут ответственность за любые задержки и/или сбои в работе АС ЦОК.
                    <br />
                    <br />
                    Ни при каких обстоятельствах СПКФР, ЦОК и/или Разработчик АС ЦОК не могут нести ответственность за любого рода ущерб (включая, но не ограничиваясь: упущенную выгоду и/или возможности, недополученную прибыль, моральный ущерб, потери по времени, простои, ущерб репутации, судебные или расходы на юристов и адвокатов итд.)
                    <br />
                    <br />
                    Настоящие Правила могут быть изменены в любое время без предварительного уведомления Соискателя. Любые такие изменения вступаю в силу немедленно и безусловно превалируют над всеми предыдущими версиями настоящих Правил. Продолжая использовать АС ЦОК после таких изменений, Вы автоматически соглашаетесь соблюдать и придерживаться новых Правил.
                    Ознакомление с последней актуальной версией Правил является обязанностью Соискателя.
                    <br />
                    <br />
                    Вы также соглашаетесь на обработку в соответствии с Федеральным законом от 27 июля 2006 г. № 152-ФЗ «О персональных данных» (Собрание законодательства Российской Федерации, 2006, № 31, ст. 3451) своих персональных данных, указанных в заявлении на прохождение независимой оценки квалификации и прилагаемых к нему документах (фамилия, имя, отчество (при наличии), дата и место рождения, реквизиты документа, удостоверяющего личность, - наименование документа, серия, номер, кем выдан и когда, месте проживания (регистрации), место работы, образование и квалификация), а также результатов прохождения профессионального экзамена, присвоения квалификации и выдачи свидетельства о квалификации, внесения и хранения соответствующей информации в реестре сведений о проведении независимой оценки квалификации в соответствии с Федеральным законом от 03.07.2016 г. № 238-ФЗ «О независимой оценке квалификации». Вы уведомлены и понимаете, что под обработкой персональных данных подразумевается совершение следующих действий (операций): сбор, обработка, запись, систематизация, накопление, хранение, уточнение (обновление, изменение), извлечение, использование, подтверждение, передача (распространение, предоставление, доступ), обезличивание, блокирование, удаление, уничтожение персональных данных по истечении срока действия настоящего согласия в соответствии с Федеральным законом от 27 июля 2006 г. № 152-ФЗ «О персональных данных».
                </span>
            </div>
            <Checkbox
              checked={complianceRules}
              className={styles.rulesCheckBox}
              label="Я ознакомился(ась) и согласен(сна) со всеми правилами проведения онлайн экзамена."
              onCheck={() => setComplianceRules(!complianceRules)}
              number="1"
              blue
            />
            <Button
              className={styles.rulesSubmitButton}
              onClick={() => generateOnlineExamRedirectionUrl(examId)}
              disabled={!complianceRules}
            >
                Подтвердить и перейти к следующему шагу
            </Button>
            {onlineExamRedirectionUrl
                ? (
                    <a
                      style={{ display: 'none' }}
                      ref={redirectionLink}
                      href={onlineExamRedirectionUrl}
                      target="_self"
                    >
                        onlineExamRedirectionUrl
                    </a>
                )
                : null}
        </WarningModal>
    );


    if (showSystem) {
        return (
            <SystemSettings
              title={(exam && exam.qualificationTitle) || ''}
              onGoBack={handleCloseShowSystem}
            />
        );
    }

    if (showRegisterInfo) {
        return (
            <RegistrationForExamContainer
              examId={examId}
              onGoBack={() => setShowRegisterInfo(false)}
            />
        );
    }

    return (
        <Card
          onClickBack={goBack}
          title={title}
          apiError={apiError}
          showLoader={processing}
          headerChild={cardHeaderBtn()}
          show404Error={!exam}
          withBackButton
          withoutPadding
          smallTitle
        >
            {exam && exam.reviewComment && exam.examApplicationStatus === ExamApplicationStatus.CorrectionsRequired
                ? <AlertText className="mb-4" title="В текущую заявку требуется внести следующие исправления:" text={exam.reviewComment} />
                : null
            }
            {exam ? (
                <ApplicantExamDetailsTabs
                  appType={AppType.Applicant}
                  examApplication={examApplication}
                  examDetails={{
                        ...exam,
                        id: exam.examId,
                        status: exam.examStatus,
                        type: exam.examType,
                        statusChangeTime: exam.applicationSubmitDate || moment(),
                        akExperts: [],
                        sovietExperts: [],
                        manualCheck: false,
                        examLogs: [],
                        qualificationId: '',
                        examinationCenterId: '',
                        qualificationEvaluationCenterId: '',
                        price: 0,
                        numberOfApplicants: 0,
                        practiceDefense: false,
                    }}
                  withoutRulesTab={!changeStatus}
                  withoutExpertsTab={!changeStatus}
                />
            ) : null}
            {renderRulesModal()}
        </Card>
    );
};

const mapState = (state: IRootState) => ({
    exam: state.exam.exam,
    examApplication: state.exam.examApplication,
    apiError: state.errors.error,
    onlineExamRedirectionUrl: state.exam.onlineExamRedirectionUrl,
});

const mapDispatch = (dispatch: Dispatch) => ({
    fetchExamDetails: dispatch.exam.fetchExamDetails,
    createExamApplication: dispatch.exam.createExamApplication,
    generateOnlineExamRedirectionUrl: dispatch.exam.generateOnlineExamRedirectionUrlAsync,
    setErrorHandler: dispatch.errors.setErrorHandler,
    onPush: (key: string) => dispatch(push(key)),
});

export default connect(mapState, mapDispatch)(ExamDetailsContainer);

interface ComponentOwnProps {
    examId: string;
}

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>
type Props = connectedProps & ComponentOwnProps;
